import React, { createContext, useContext, useState, useCallback } from 'react';
import {
    strings,
    interpolate,
    type Locale,
    type Strings,
    DEFAULT_LOCALE,
    SUPPORTED_LOCALES,
} from '../i18n';

/** 語言偏好僅存在 localStorage，不寫入資料庫，下次開啟時自動還原 */
const LOCALE_STORAGE_KEY = 'courseyvoyage-locale';

/** 依系統地區決定預設語言：台灣→繁體中文、日本→日文、其他→英文 */
function getLocaleFromSystem(): Locale {
    try {
        const nav = navigator as { language?: string; languages?: readonly string[] };
        const lang = nav.language || nav.languages?.[0] || '';
        const lower = lang.toLowerCase();
        // 台灣：zh-TW, zh-Hant-TW, zh-Hant（繁體中文）
        if (lower.startsWith('zh-tw') || lower.startsWith('zh-hant-tw') || lower === 'zh-hant') return 'zh-TW';
        // 日本
        if (lower.startsWith('ja')) return 'ja';
    } catch (_) { }
    return 'en';
}

function getStoredLocale(): Locale {
    try {
        const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
        const validCodes = SUPPORTED_LOCALES.map(l => l.code);
        if (stored && validCodes.includes(stored as Locale)) return stored as Locale;
    } catch (_) { }
    return getLocaleFromSystem();
}

export type TFunction = (key: keyof Strings, params?: Record<string, string | number>) => string;

interface I18nContextType {
    locale: Locale;
    setLocale: (locale: Locale) => Promise<void>;
    t: TFunction;
    supportedLocales: typeof SUPPORTED_LOCALES;
}

const I18nContext = createContext<I18nContextType>({
    locale: DEFAULT_LOCALE,
    setLocale: async () => { },
    t: (k) => k as string,
    supportedLocales: SUPPORTED_LOCALES,
});

export const useI18n = () => useContext(I18nContext);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [locale, setLocaleState] = useState<Locale>(getStoredLocale);

    const setLocale = useCallback(async (newLocale: Locale) => {
        setLocaleState(newLocale);
        localStorage.setItem(LOCALE_STORAGE_KEY, newLocale);
    }, []);

    const t: TFunction = useCallback((key, params) => {
        const s = strings[locale];
        const raw = s?.[key as keyof Strings] ?? key;
        if (params && typeof raw === 'string') {
            return interpolate(raw, params);
        }
        return (raw as string) ?? (key as string);
    }, [locale]);

    const value: I18nContextType = {
        locale,
        setLocale,
        t,
        supportedLocales: SUPPORTED_LOCALES,
    };

    return (
        <I18nContext.Provider value={value}>
            {children}
        </I18nContext.Provider>
    );
};
